"use client";

import { FC, useEffect, useRef, useState } from "react";
import ImageScoreCard from "./components/ImageScoreCard";
import { ImageResponseData } from "@/restapi/image";
import InfiniteScroll from "react-infinite-scroll-component";
import { usePathname } from "next/navigation";
import { FilterImageCategory } from "../hooks/useGalleryCategory";
import { User } from "next-auth";

// 上下それぞれに表示可能な件数
const VISIBLE_IMAGE_BUFFER = 30

/**
 * 全画面表示ギャラリー
 */
const FullScreenGallery: FC<{
  selectedImageId?: string;
  images: ImageResponseData[];
  loadMore: () => void;
  hasMore: boolean;
  filterImageCategory: FilterImageCategory;
  user?: User
  revertScreenType: () => void;
}> = ({ images, selectedImageId, filterImageCategory, loadMore, hasMore, revertScreenType, user }) => {
  const pathname = usePathname();
  const ref = useRef<HTMLDivElement>(null);
  const initialized = useRef(false);
  const [items, setItems] = useState<ImageResponseData[]>([]);
  const [prevImages, setPrevImages] = useState<ImageResponseData[]>([]);
  const isScrolling = useRef(false);
  const lastScrollY = useRef(0);
  const [scrolled, setScrolled] = useState(false);

  const [activeUuid, setActiveUuid] = useState<string | null>(null);
  const [audioEnabled, setAudioEnabled] = useState(false);

  const kickFirstVideoAfterJump = (selectedImageId: string) => {
    const findVideo = () => {
      const wrapper = document.getElementById(`image-${selectedImageId}`);
      if (!wrapper) return null;
      return wrapper.querySelector("video") as HTMLVideoElement | null;
    };

    const tryPlay = () => {
      const v = findVideo();
      if (!v) return;

      // autoplay成立のため最初は muted を固定（音は後でユーザー操作で解除）
      v.muted = false;
      v.playsInline = true;
      v.autoplay = true;
      v.loop = true;
      v.preload = "auto";
      v.controls = true;

      try {
        const p = v.play();
        if (p && typeof (p as any).catch === "function") {
          (p as Promise<void>).catch(() => { });
        }
      } catch { }
    };

    // iOSの「scroll直後は失敗する」対策：時間差で複数回
    requestAnimationFrame(tryPlay);        // 次フレーム
    setTimeout(tryPlay, 100);             // 0.1秒後
    setTimeout(tryPlay, 250);             // 0.25秒後
    setTimeout(tryPlay, 500);             // 0.5秒後
    setTimeout(tryPlay, 900);             // 0.9秒後（保険）
  };

  useEffect(() => {
    if (!ref.current) return;

    const root = ref.current;

    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => (b.intersectionRatio ?? 0) - (a.intersectionRatio ?? 0));

        const top = visible[0];
        if (!top) return;

        const id = top.target.getAttribute("id");
        if (!id) return;

        const uuid = id.replace("image-", "");
        setActiveUuid(uuid);
      },
      {
        root,
        threshold: [0.55],
      }
    );

    // items のDOMを監視
    const nodes = root.querySelectorAll('[id^="image-"]');
    nodes.forEach((n) => observer.observe(n));

    return () => observer.disconnect();
  }, [items]);

  useEffect(() => {
    if (!selectedImageId || images.length === 0) return;

    const selectedIndex = images.findIndex((img) => img.uuid === selectedImageId);
    if (selectedIndex === -1) return;

    const startImages = images.slice(Math.max(0, selectedIndex - VISIBLE_IMAGE_BUFFER), selectedIndex + VISIBLE_IMAGE_BUFFER + 1);
    setItems(startImages);

    setPrevImages(images.slice(0, Math.max(0, selectedIndex - 10)));
  }, [selectedImageId, images]);

  /** スムーズに選択画像までスクロール（位置補正） */
  useEffect(() => {
    if (!selectedImageId || initialized.current) return;
    initialized.current = true;
    setTimeout(() => {
      const targetElement = document.getElementById(`image-${selectedImageId}`);
      if (targetElement) {
        isScrolling.current = true;
        targetElement.scrollIntoView({ behavior: "auto", block: "center" });

        setActiveUuid(selectedImageId);

        kickFirstVideoAfterJump(selectedImageId);

        setTimeout(() => {
          isScrolling.current = false;
        }, 600);
      }
      setScrolled(true);
    }, 500);
  }, [selectedImageId]);

  /** 上スクロール時に `prevImages` を `items` に追加し、スクロール位置を適切に補正 */
  useEffect(() => {
    if (!ref.current) return;

    const currentRef = ref.current;

    const handleScroll = () => {
      if (!currentRef || isScrolling.current) return;

      const scrollTop = currentRef.scrollTop;
      const scrollDelta = scrollTop - lastScrollY.current;

      // **上スクロール時に画像を追加**
      if (scrollDelta < 0 && scrollTop < 50 && prevImages.length > 0) {
        const addCount = Math.min(5, prevImages.length);
        const newItems = prevImages.slice(-addCount);
        const previousHeight = currentRef.scrollHeight; // 追加前の高さ

        setItems((prev) => [...newItems, ...prev]);

        setPrevImages((prevState) => {
          const updatedPrev = prevState.slice(0, -addCount);
          return updatedPrev;
        });

        // **追加後にスクロール位置を補正**
        setTimeout(() => {
          const addedHeight = currentRef.scrollHeight - previousHeight;
          currentRef.scrollTop += addedHeight; // 高さ増加分を補正
        }, 1000);
      }

      lastScrollY.current = scrollTop;
    };

    // **requestAnimationFrame で最適化**
    let ticking = false;
    const smoothScrollHandler = () => {
      if (!ticking) {
        requestAnimationFrame(() => {
          handleScroll();
          ticking = false;
        });
        ticking = true;
      }
    };

    currentRef.addEventListener("scroll", smoothScrollHandler);

    return () => {
      currentRef.removeEventListener("scroll", smoothScrollHandler);
    };
  }, [prevImages]);

  return (
    <div data-testid="fullscreen-gallery" ref={ref} className={`${scrolled ? 'opacity-100' : 'opacity-0'} w-full min-h-screen transition-opacity duration-200 overflow-y-auto`}>
      <InfiniteScroll
        dataLength={items.length}
        next={loadMore}
        hasMore={hasMore}
        loader={<div className="loader"></div>}
        inverse={true}
      >
        <div className="slide" id="scrollable-div">
          {items.length === 0 && (
            <div className="text-white text-center p-10">画像が見つかりません</div>
          )}
          {items.map((img) => (
            <div
              key={img.uuid}
              id={`image-${img.uuid}`}
              className="snap-item transition-all duration-500 transform"
            >
              <ImageScoreCard image={img}
                isActive={activeUuid === img.uuid}
                audioEnabled={audioEnabled}
              />
            </div>
          ))}
        </div>
      </InfiniteScroll>
    </div>
  );
};

export default FullScreenGallery;